import axios from 'axios';
import { debugApiUrl, fixApiUrlForProduction } from '../utils/debugApiUrl';

// Check if we're in development mode
const isDevelopment = import.meta.env.DEV;

console.log('💡 Development mode:', isDevelopment);

// Debug API URL to help diagnose issues
const apiDebugInfo = debugApiUrl();

// Determine the appropriate API URL - in production, this will use the current host
const apiBaseUrl = fixApiUrlForProduction();
console.log('🔌 Using API base URL:', apiBaseUrl);

// Create axios instance with base URL from environment
const api = axios.create({
  baseURL: apiBaseUrl,
  withCredentials: true, // Allow cookies to be sent with requests (needed for authentication)
  timeout: 10000, // 10 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for adding auth headers and CSRF token
api.interceptors.request.use(
  async (config) => {
    // For GET requests, no CSRF token is needed
    if (config.method?.toLowerCase() === 'get') {
      return config;
    }

    try {
      // CSRF Test Mode - Emergency rollback capability
      if (import.meta.env.VITE_CSRF_DISABLED === 'true') {
        console.log(`⚠️ CSRF tokens disabled via VITE_CSRF_DISABLED: ${config.method.toUpperCase()} ${config.url}`);
        return config;
      }
      
      // Skip CSRF token in development with auth bypass
      if (import.meta.env.VITE_BYPASS_AUTH === 'true') {
        console.log(`⚠️ CSRF tokens disabled via VITE_BYPASS_AUTH: ${config.method.toUpperCase()} ${config.url}`);
        return config;
      }

      // Fetch CSRF token if we don't have one in local storage
      let csrfToken = localStorage.getItem('csrfToken');
      
      if (!csrfToken) {
        // Use a separate axios instance to avoid infinite loop
        console.log('Fetching CSRF token from:', config.baseURL + '/csrf-token');
        const csrfResponse = await axios.create({
          baseURL: config.baseURL,
          withCredentials: true,
          timeout: 5000
        }).get('/csrf-token');
        
        // Handle the standardized response format
        if (csrfResponse.data && csrfResponse.data.success && csrfResponse.data.data) {
          csrfToken = csrfResponse.data.data.csrfToken;
        } else if (csrfResponse.data && csrfResponse.data.csrfToken) {
          csrfToken = csrfResponse.data.csrfToken;
        } else {
          console.error('🚨 Unexpected CSRF token response format:', csrfResponse.data);
          throw new Error('Invalid CSRF token response format');
        }
        
        localStorage.setItem('csrfToken', csrfToken);
      }
      
      // Add token to headers
      config.headers['X-CSRF-Token'] = csrfToken;
      console.log(`🔒 Added CSRF token to ${config.method.toUpperCase()} ${config.url}`);
    } catch (error) {
      console.error('🚨 CSRF token fetch failed:', {
        error: error.message,
        status: error.response?.status,
        url: config.url,
        method: config.method
      });
      
      // In development, we can proceed without CSRF for debugging
      if (import.meta.env.DEV) {
        console.warn('⚠️ Development mode: proceeding without CSRF token');
        return config;
      }
      
      // In production, CSRF token failures should fail the request
      throw new Error(`CSRF protection failed: ${error.message}. Please refresh the page.`);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Skip transformation for endpoints that need full response structure (pagination, meta, etc.)
    const skipTransformEndpoints = [
      '/equipment-items',
      '/inventories',
      '/plans',
      '/barcode/templates'
    ];
    
    const shouldSkipTransform = skipTransformEndpoints.some(endpoint => 
      response.config.url?.includes(endpoint)
    );
    
    // Only transform if not a pagination endpoint
    if (response.data?.success === true && !shouldSkipTransform) {
      response.data = response.data.data;
    }
    return response;
  },
  async (error) => {
    // Handle 401 Unauthorized - clear auth state and redirect to login
    if (error.response && error.response.status === 401) {
      // Check if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
      return Promise.reject('Authentication required. Please log in.');
    }
    
    // Handle 403 Forbidden - refresh CSRF token and retry once
    if (error.response && error.response.status === 403 && 
        (error.response.data?.error?.includes('CSRF') || 
         error.response.data?.message?.includes('CSRF'))) {
      
      console.warn('🔄 CSRF token validation failed, attempting retry:', {
        url: error.config?.url,
        method: error.config?.method,
        hasRetried: !!error.config?._retry
      });
      
      // Clear stored token
      localStorage.removeItem('csrfToken');
      
      // Only retry non-GET requests once
      if (error.config && !error.config._retry && error.config.method !== 'get') {
        error.config._retry = true;
        console.log('🔒 Fetching fresh CSRF token and retrying request...');
        
        try {
          // Fetch a fresh CSRF token
          const csrfResponse = await axios.create({
            baseURL: error.config.baseURL || api.defaults.baseURL,
            withCredentials: true,
            timeout: 5000
          }).get('/csrf-token');
          
          // Handle the standardized response format
          let newCsrfToken;
          if (csrfResponse.data && csrfResponse.data.success && csrfResponse.data.data) {
            newCsrfToken = csrfResponse.data.data.csrfToken;
          } else if (csrfResponse.data && csrfResponse.data.csrfToken) {
            newCsrfToken = csrfResponse.data.csrfToken;
          } else {
            console.error('🚨 Unexpected CSRF token response format on retry:', csrfResponse.data);
            throw new Error('Invalid CSRF token response format');
          }
          
          localStorage.setItem('csrfToken', newCsrfToken);
          
          // Update the request config with the new token
          error.config.headers['X-CSRF-Token'] = newCsrfToken;
          
          console.log('🔄 Retrying request with fresh CSRF token');
          return api.request(error.config);
        } catch (csrfError) {
          console.error('🚨 Failed to fetch fresh CSRF token:', csrfError);
          return Promise.reject('Failed to refresh CSRF token. Please refresh the page.');
        }
      }
      
      // After retry failed or for GET requests
      console.error('🚨 CSRF validation failed after retry');
      return Promise.reject('CSRF token validation failed. Your session may have expired. Please refresh the page and try again.');
    }
    
    // Handle network errors
    if (!error.response) {
      return Promise.reject('Network error. Please check your connection.');
    }
    
    // For all other errors, use standardized error format
    const errorMessage = error.response.data?.error || error.message;
    console.error('API Error Details:', {
      status: error.response?.status,
      data: error.response?.data,
      message: errorMessage,
      fullResponse: JSON.stringify(error.response?.data, null, 2),
      requestData: error.config?.data ? JSON.parse(error.config.data) : null
    });
    
    // Return the full error response data, not just the message
    return Promise.reject({
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data,
      errors: error.response?.data?.errors
    });
  }
);

// Export api instance
export default api;

// Auth API - Simplified (matching what AuthContext expects)
export const authApi = {
  checkAuthStatus: () => api.get('/auth/status'),
  logout: () => api.post('/auth/logout'),
  getLoginUrl: () => `${api.defaults.baseURL}/auth/google/login`,
  
  // Additional methods for completeness
  login: async (credentials) => {
    return api.post('/auth/login', credentials);
  },
  
  register: async (userData) => {
    return api.post('/auth/register', userData);
  },
  
  getProfile: async () => {
    return api.get('/auth/profile');
  },
  
  updateProfile: async (updates) => {
    return api.put('/auth/profile', updates);
  },
  
  changePassword: async (passwordData) => {
    return api.put('/auth/change-password', passwordData);
  },
  
  googleCallback: async (code) => {
    return api.get(`/auth/google/callback?code=${code}`);
  },
  
  // Refresh session
  refreshSession: async () => {
    return api.post('/auth/refresh');
  },
  
  // Get user sessions
  getUserSessions: async () => {
    return api.get('/auth/sessions');
  },
};

// V2 Equipment Items API (Primary interface)
export const equipmentApi = {
  // Get equipment items summary (V2)
  getEquipmentSummary: async (options = {}) => {
    return api.get('equipment-items', { params: options });
  },
  
  // Search equipment items (V2)
  searchEquipment: async (params) => {
    // Convert query param to search param for the API
    const searchParams = { ...params };
    if (searchParams.query) {
      searchParams.search = searchParams.query;
      delete searchParams.query;
    }
    return api.get('equipment-items', { params: searchParams });
  },
  
  // Get all equipment items (V2)
  getAllEquipment: async (params = {}) => {
    return api.get('equipment-items', { params });
  },
  
  // Get available equipment items (V2)
  getAvailableEquipment: async (params = {}) => {
    return api.get('equipment-items', { params: { ...params, status: 'available' } });
  },
  
  // Get single equipment item (V2)
  getEquipmentItem: async (id) => {
    return api.get(`equipment-items/${id}`);
  },
  
  // Create equipment items (V2)
  createEquipmentItems: async (items) => {
    return api.post('equipment-items', { items });
  },
  
  // Update equipment item (V2)
  updateEquipmentItem: async (id, updates) => {
    return api.put(`equipment-items/${id}`, updates);
  },
  
  // Delete equipment item (V2)
  deleteEquipmentItem: async (id) => {
    return api.delete(`equipment-items/${id}`);
  },
  
  // Get equipment status history (V2)
  getStatusHistory: async (id) => {
    return api.get(`equipment-items/${id}/status-history`);
  },
  
  // Assign equipment to job (V2 - direct equipment update)
  assignToJob: async (equipmentId, jobData) => {
    return api.put(`equipment-items/${equipmentId}`, {
      current_job_id: jobData.job_id,
      current_site_id: jobData.site_id,
      status: 'assigned',
      notes: jobData.notes
    });
  },
  
  // Unassign equipment from job (V2 - direct equipment update)
  unassignFromJob: async (equipmentId, unassignData = {}) => {
    return api.put(`equipment-items/${equipmentId}`, {
      current_job_id: null,
      current_site_id: null,
      status: 'available',
      notes: unassignData.notes || 'Returned to inventory'
    });
  },
  
  // Update equipment status (V2)
  updateStatus: async (id, statusData) => {
    return api.put(`equipment-items/${id}`, statusData);
  },
  
  // Create equipment template
  createEquipmentTemplate: async (templateData) => {
    return api.post('equipment-templates', templateData);
  },
  
  // Get equipment templates
  getEquipmentTemplates: async (params = {}) => {
    return api.get('equipment-templates', { params });
  },
  
  // Update equipment template
  updateEquipmentTemplate: async (id, templateData) => {
    return api.put(`equipment-templates/${id}`, templateData);
  },
  
  // Delete equipment template
  deleteEquipmentTemplate: async (id) => {
    return api.delete(`equipment-templates/${id}`);
  },
};

// Legacy Inventory API (for backwards compatibility)
export const inventoryApi = {
  // Get inventory summary - redirects to V2
  getInventorySummary: async (options = {}) => {
    return equipmentApi.getEquipmentSummary(options);
  },
  
  // Search inventory - redirects to V2
  searchInventory: async (params) => {
    return equipmentApi.searchEquipment(params);
  },
  
  // Get all inventory items - redirects to V2
  getAllInventory: async () => {
    return equipmentApi.getAllEquipment();
  },
  
  // Create inventory item - redirects to V2
  createInventoryItem: async (itemData) => {
    return equipmentApi.createEquipmentItems([itemData]);
  },
  
  // Update inventory item - redirects to V2
  updateInventoryItem: async (id, updates) => {
    return equipmentApi.updateEquipmentItem(id, updates);
  },
  
  // Delete inventory item - redirects to V2
  deleteInventoryItem: async (id) => {
    return equipmentApi.deleteEquipmentItem(id);
  },
  
  // Process transfer - V2 architecture using direct equipment update
  processTransfer: async (transferData) => {
    // Try multiple ways to get the equipment ID
    const equipmentId = transferData.items?.[0]?.id || 
                       transferData.items?.[0]?.equipment_id || 
                       transferData.equipmentId ||
                       transferData.equipment_id;
    
    if (!equipmentId) {
      console.error('Transfer data structure:', JSON.stringify(transferData, null, 2));
      throw new Error('Equipment ID is required for transfer. Check transfer data structure.');
    }
    
    // V2 implementation: Direct equipment update via PUT /api/equipment-items/:id
    return api.put(`/equipment-items/${equipmentId}`, {
      current_job_id: transferData.toJobId || transferData.jobId || null, // V2 direct job assignment
      current_site_id: transferData.toSiteId || null, // V2 direct site assignment
      status: transferData.toJobId ? 'assigned' : 'available',
      updated_by: transferData.transferredBy || 'user',
      notes: transferData.notes || `Transfer: ${transferData.fromLocation} → ${transferData.toLocation}`
    });
  },
  
  // Process checkout
  processCheckout: async (checkoutData) => {
    return api.post('/inventories/checkout', checkoutData);
  },
  
  // Process return
  processReturn: async (returnData) => {
    return api.post('/inventories/return', returnData);
  },
  
  // Get transactions
  getTransactions: async (params) => {
    return api.get('/inventories/transactions', { params });
  },
  
  // Receive items
  receiveItems: async (items) => {
    return api.post('/inventories/receive', { items });
  },
  
  // Receive items (V2 compatible)
  receiveItems: async (items) => {
    return equipmentApi.createEquipmentItems(items);
  },
  
  // Checkout items using V2 assignment
  checkoutItems: async (items, jobNumber, options = {}) => {
    const assignments = items.map(item => ({
      equipment_id: item.equipment_id || item.id,
      job_id: jobNumber,
      ...options
    }));
    
    const results = await Promise.all(
      assignments.map(assignment => 
        equipmentApi.assignToJob(assignment.equipment_id, assignment)
      )
    );
    
    return { data: { results } };
  },
  
  // Return items using V2 unassignment
  returnItems: async (items, jobNumber, options = {}) => {
    const unassignments = items.map(item => ({
      equipment_id: item.equipment_id || item.id,
      job_id: jobNumber,
      ...options
    }));
    
    const results = await Promise.all(
      unassignments.map(unassignment => 
        equipmentApi.unassignFromJob(unassignment.equipment_id, unassignment)
      )
    );
    
    return { data: { results } };
  },

  // Return equipment from job (V2)
  returnFromJob: async (equipmentId, returnData) => {
    return api.put(`equipment-items/${equipmentId}`, {
      status: 'available',
      current_job_id: null,
      current_site_id: null,
      warehouse_id: returnData.warehouse_id,
      zone_id: returnData.zone_id,
      notes: returnData.notes || 'Returned to warehouse'
    });
  },

  // Location Management APIs
  getWarehouses: async () => {
    return api.get('inventories/warehouses');
  },


  getRacks: async (warehouseId) => {
    return api.get(`inventories/racks?warehouseId=${warehouseId}`);
  },

  getBays: async (rackId, warehouseId) => {
    return api.get(`inventories/bays?rackId=${rackId}&warehouseId=${warehouseId}`);
  },
  
  // Archive equipment (V2)
  archiveEquipment: async (id, reason) => {
    return api.put(`/equipment-items/${id}`, { status: 'archived', notes: reason });
  },
  
  // Restore equipment (V2)
  restoreEquipment: async (id, reason) => {
    return api.put(`/equipment-items/${id}`, { status: 'available', notes: reason });
  },
  
  
  // Transfer between jobs
  transferBetweenJobs: async (transferData) => {
    return api.post('/inventories/transfer-between-jobs', transferData);
  },

  // Transfer equipment (V2 - direct equipment update)
  transferEquipment: async (transferData) => {
    return api.put(`/equipment-items/${transferData.equipment_id}`, {
      current_job_id: transferData.destination_job_id,
      current_site_id: transferData.destination_site_id,
      status: transferData.destination_job_id ? 'assigned' : 'available',
      notes: transferData.reason || transferData.notes
    });
  },
};

// Sheets API (legacy support)
export const sheetsApi = {
  listSpreadsheets: async () => {
    console.warn('Google Sheets integration is deprecated. This returns empty data.');
    return { data: [] };
  },
  
  getSheetData: async (spreadsheetId, range) => {
    console.warn('Google Sheets integration is deprecated. Use inventory API instead.');
    // Redirect to inventory API
    return inventoryApi.getAllInventory();
  },
  
  updateSheet: async (spreadsheetId, range, values) => {
    console.warn('Google Sheets integration is deprecated. Use inventory API instead.');
    return { data: { status: 'success', message: 'Please use inventory API' } };
  },
  
  appendToSheet: async (spreadsheetId, range, values) => {
    console.warn('Google Sheets integration is deprecated. Use inventory API instead.');
    return { data: { status: 'success', message: 'Please use inventory API' } };
  },
};

// Job API
export const jobApi = {
  // Get all jobs
  getAllJobs: async (params = {}) => {
    return api.get('/jobs', { params });
  },
  
  // Alias for getAllJobs (for compatibility)
  getJobs: async (params = {}) => {
    return api.get('/jobs', { params });
  },
  
  // Get job by ID
  getJobById: async (jobId) => {
    return api.get(`/jobs/${jobId}`);
  },
  
  // Create new job
  createJob: async (jobData) => {
    return api.post('/jobs', jobData);
  },
  
  // Update job
  updateJob: async (jobId, updates) => {
    return api.put(`/jobs/${jobId}`, updates);
  },
  
  // Delete job
  deleteJob: async (jobId) => {
    return api.delete(`/jobs/${jobId}`);
  },
  
  // Get job equipment
  getJobEquipment: async (jobId, params = {}) => {
    return api.get(`/jobs/${jobId}/equipment`, { params });
  },
  
  // Update equipment site assignment (V2)
  updateEquipmentSite: async (jobId, equipmentId, siteId) => {
    return api.put(`/equipment-items/${equipmentId}`, { 
      current_job_id: jobId,
      current_site_id: siteId,
      status: 'assigned'
    });
  },
  
  // Get job sites
  getJobSites: async (jobId) => {
    return api.get(`/jobs/${jobId}/sites`);
  },
  
  // Add/Update job equipment
  updateJobEquipment: async (jobId, siteId, equipmentData) => {
    return api.put(`/jobs/${jobId}/sites/${siteId}/equipment`, equipmentData);
  },
  
  // Delete job equipment (V2 - unassign equipment)
  deleteJobEquipment: async (jobId, siteId, equipmentId) => {
    return api.put(`/equipment-items/${equipmentId}`, {
      current_job_id: null,
      current_site_id: null,
      status: 'available'
    });
  },
  
  // Create job site
  createJobSite: async (jobId, siteData) => {
    return api.post(`/jobs/${jobId}/sites`, siteData);
  },
  
  // Update job site
  updateJobSite: async (jobId, siteId, updates) => {
    return api.put(`/jobs/${jobId}/sites/${siteId}`, updates);
  },
  
  // Delete job site
  deleteJobSite: async (jobId, siteId) => {
    return api.delete(`/jobs/${jobId}/sites/${siteId}`);
  },
  
  // Update job status
  updateJobStatus: async (jobId, status) => {
    return api.patch(`/jobs/${jobId}/status`, { status });
  },
  
  // Get job sites
  getJobSites: async (jobId) => {
    return api.get(`/jobs/${jobId}/sites`);
  },
};

// User Management API
export const userApi = {
  // Get all users
  getAllUsers: async () => {
    return api.get('/auth/users');
  },
  
  // Get user by ID
  getUserById: async (userId) => {
    return api.get(`/auth/users/${userId}`);
  },
  
  // Update user role
  updateUserRole: async (userId, role) => {
    return api.put(`/auth/users/${userId}/role`, { role });
  },
  
  // Delete user
  deleteUser: async (userId) => {
    return api.delete(`/auth/users/${userId}`);
  },
};

// Site API
export const siteApi = {
  // Get all sites
  getAllSites: async () => {
    return api.get('/sites');
  },
  
  // Get site by ID
  getSiteById: async (siteId) => {
    return api.get(`/sites/${siteId}`);
  },
  
  // Create site
  createSite: async (siteData) => {
    return api.post('/sites', siteData);
  },
  
  // Update site
  updateSite: async (siteId, updates) => {
    return api.put(`/sites/${siteId}`, updates);
  },
  
  // Delete site
  deleteSite: async (siteId) => {
    return api.delete(`/sites/${siteId}`);
  },
};

// Error reporting API
export const errorApi = {
  // Report an error to the server
  reportError: async (errorData) => {
    return api.post('/errors/report', errorData);
  },
  
  // Get error logs (admin only)
  getErrorLogs: async (params) => {
    return api.get('/errors/logs', { params });
  },
};

// Planning API (for job equipment planning)
export const planningApi = {
  // Get equipment plans for a job
  getJobEquipmentPlans: async (jobId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = `/plans/jobs/${jobId}/plans${queryString ? `?${queryString}` : ''}`;
    return api.get(url);
  },
  
  // Create equipment plan
  createEquipmentPlan: async (jobId, planData) => {
    return api.post(`/plans/jobs/${jobId}/plans`, planData);
  },
  
  // Update equipment plan
  updateEquipmentPlan: async (planId, updateData) => {
    return api.patch(`/plans/equipment/${planId}`, updateData);
  },
  
  // Delete equipment plan
  deleteEquipmentPlan: async (jobId, planId) => {
    return api.delete(`/plans/jobs/${jobId}/plans/${planId}`);
  },
  
  // Get equipment library (for planning)
  getEquipmentLibrary: async (params = {}) => {
    return api.get('/plans/library', { params });
  },
  
  // Get equipment templates (V2 - derived from actual equipment)
  getEquipmentCatalogs: async () => {
    return api.get('equipment-items', { params: { distinct: 'description' } });
  },
  
  // Create equipment order
  createEquipmentOrder: async (orderData) => {
    return api.post('/plans/orders', orderData);
  },
  
  // Update order status
  updateOrderStatus: async (orderId, status) => {
    return api.patch(`/plans/orders/${orderId}`, { status });
  },
  
  // Get shortages report
  getShortages: async (jobId) => {
    const params = jobId ? { jobId } : {};
    return api.get('/plans/shortages', { params });
  },
  
  // Get individual equipment items for a plan
  getPlanEquipmentItems: async (planId) => {
    return api.get(`/plans/${planId}/items`);
  }
};

// Barcode API
export const barcodeApi = {
  // Lookup equipment by barcode, equipment_id, or database ID
  lookupByBarcode: async (identifier) => {
    return api.get(`barcode/lookup?barcode=${encodeURIComponent(identifier)}`);
  },
  
  // Generate barcode for equipment
  generateBarcode: async (equipmentId) => {
    return api.post(`barcode/generate`, { equipment_id: equipmentId });
  },
};

// Status mapping utility
export const statusMapping = {
  
  // Get status color for UI
  getStatusColor: (status) => {
    const normalizedStatus = status.toLowerCase();
    const statusColors = {
      'available': 'success',
      'assigned': 'primary',
      'maintenance': 'warning',
      'archived': 'secondary',
      'unavailable': 'danger'
    };
    return statusColors[normalizedStatus] || 'default';
  }
};

// Export helper functions from the old service (updated for V2)
export const {
  getSheetData,
  searchInventory,
  appendToSheet,
  loadInventorySummary
} = {
  getSheetData: async () => {
    const response = await equipmentApi.getAllEquipment();
    return response.data || [];
  },
  
  searchInventory: async (params) => {
    const response = await equipmentApi.searchEquipment(params);
    return response.data || [];
  },
  
  appendToSheet: async (spreadsheetId, range, values) => {
    console.warn('appendToSheet is deprecated. Use equipment API methods.');
    return { status: 'success' };
  },
  
  loadInventorySummary: async (options) => {
    const response = await equipmentApi.getEquipmentSummary(options);
    return response.data.data || response.data;
  }
};

// Job Assignment API (PO workflow)
export const jobAssignmentApi = {
  // Direct PO assignment
  assignEquipmentByPO: (data) =>
    api.post('/job-assignment/assign-po', data),

  // Quick barcode scanning updates
  updateStackByBarcode: (data) =>
    api.post('/job-assignment/scan-update', data),

  // Get job stack summary
  getJobStack: (jobId) =>
    api.get(`/job-assignment/${jobId}/stack`),

  // Get all job stacks
  getAllJobStacks: (params = {}) =>
    api.get('/job-assignment/stacks', { params })
};